// src/contexts/EmpresaContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { useAuth } from "./AuthContext";
import { nivelUsuario } from "../utils/permissoes";
import { NIVEIS } from "../config/niveis";
import { db } from "../config/firebase";

const EmpresaContext = createContext(null);

export function EmpresaProvider({ children }) {
  const { usuario } = useAuth();
  const [modoGlobal, setModoGlobal] = useState(true);
  // empresaAtual: null | { id?: string|null, nome: string, logoUrl?: string|null }
  const [empresaAtual, setEmpresaAtual] = useState(null);

  // Carrega preferência salva (se existir) SEMPRE que trocar de usuário
  useEffect(() => {
    if (!usuario) {
      setModoGlobal(false);
      setEmpresaAtual(null);
      return;
    }

    const storageKey = `mamute_escopo_empresa_${usuario.uid || "anon"}`;
    const salvoRaw = window.localStorage.getItem(storageKey);

    const nivel = nivelUsuario(usuario);

    const padraoGlobal =
      nivel >= NIVEIS.TECNICO_SENIOR; // Técnico Sênior, Admin, Gestor

    if (salvoRaw) {
      try {
        const salvo = JSON.parse(salvoRaw);

        // Compatibilidade retroativa:
        // - versões antigas salvavam empresaAtual como string (nome)
        // - versão nova salva como objeto {id, nome}
        let empresa = salvo.empresaAtual ?? null;
        if (typeof empresa === "string") {
          empresa = { id: null, nome: empresa };
        }

        // Sanitiza objeto
        if (empresa && typeof empresa === "object") {
          const nome = String(empresa.nome ?? "").trim();
          empresa = nome ? { id: empresa.id ?? null, nome, logoUrl: empresa.logoUrl ?? null } : null;
        }

        setModoGlobal(!!salvo.modoGlobal);
        setEmpresaAtual(empresa);
        return;
      } catch (e) {
        console.warn("Falha ao ler escopo de empresa salvo:", e);
      }
    }

    // Sem configuração salva: define padrão
    if (padraoGlobal) {
      setModoGlobal(true);
      setEmpresaAtual(null);
      return;
    }

    // Para níveis baixos (Cliente/Kiosk), fixa em uma empresa padrão do próprio usuário
    setModoGlobal(false);

    let alive = true;
    (async () => {
      try {
        const grupoId = usuario.grupoId || "";
        // Preferência de empresa para perfis baixos (Cliente/Kiosk):
        // 1) usuario.empresaId (modelo novo)
        // 2) se houver exatamente 1 empresa permitida (por id), usa ela
        // 3) fallback por nome (modelo antigo)

        const detalhes = Array.isArray(usuario.empresasPermitidasDetalhes)
          ? usuario.empresasPermitidasDetalhes
          : [];

        const permitidasRaw = Array.isArray(usuario.empresasPermitidas)
          ? usuario.empresasPermitidas
          : [];

        const empresaUnicaDetalhe = detalhes.length === 1 ? detalhes[0] : null;

        let empresaIdUsuario = usuario.empresaId || null;
        if (!empresaIdUsuario) {
          if (empresaUnicaDetalhe?.id) {
            empresaIdUsuario = empresaUnicaDetalhe.id;
          } else if (permitidasRaw.length === 1) {
            const v = permitidasRaw[0];
            empresaIdUsuario = typeof v === "string" ? v : v?.id || null;
          }
        }

        const nomeEmpresa = String(
          usuario.empresaNome ||
            empresaUnicaDetalhe?.nome ||
            usuario.empresa ||
            ""
        ).trim();

        if (!grupoId) {
          setEmpresaAtual(null);
          return;
        }

        // Preferência: se o usuário já tiver empresaId, usa direto (evita falhas por nome).
        if (empresaIdUsuario) {
          try {
            const ref = doc(db, "empresas", empresaIdUsuario);
            const snap = await getDoc(ref);

            if (!alive) return;

            if (snap.exists()) {
              const data = snap.data() || {};
              // Segurança: só aceita se for do mesmo grupo
              if (!data.grupoId || data.grupoId === grupoId) {
                setEmpresaAtual({
                  id: snap.id,
                  nome: data.nome || nomeEmpresa || "Empresa",
                  logoUrl: data.logoUrl || null,
                });
                return;
              }
            }
          } catch (e) {
            console.warn("Falha ao carregar empresa por empresaId:", e);
          }
        }

        // Fallback: tenta obter o ID real da empresa pelo nome (compatibilidade).
        if (!nomeEmpresa) {
          setEmpresaAtual(null);
          return;
        }

        const q = query(
          collection(db, "empresas"),
          where("grupoId", "==", grupoId),
          where("nome", "==", nomeEmpresa)
        );
        const snap = await getDocs(q);

        if (!alive) return;

        if (!snap.empty) {
          const d = snap.docs[0];
          const data = d.data() || {};
          setEmpresaAtual({
            id: d.id,
            nome: data.nome || nomeEmpresa,
            logoUrl: data.logoUrl || null,
          });
        } else {
          setEmpresaAtual({ id: null, nome: nomeEmpresa, logoUrl: null });
        }
      } catch (e) {
        console.warn("Falha ao definir empresa padrão:", e);
        if (!alive) return;
        const nomeEmpresa = String(usuario.empresa || "").trim();
        setEmpresaAtual(nomeEmpresa ? { id: null, nome: nomeEmpresa, logoUrl: null } : null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [usuario]);

  const salvarPreferencia = (novoModoGlobal, novaEmpresa) => {
    if (!usuario) return;
    const storageKey = `mamute_escopo_empresa_${usuario.uid || "anon"}`;

    let empresa = novaEmpresa || null;
    if (typeof empresa === "string") {
      empresa = { id: null, nome: empresa };
    }
    if (empresa && typeof empresa === "object") {
      const nome = String(empresa.nome ?? "").trim();
      empresa = nome ? { id: empresa.id ?? null, nome, logoUrl: empresa.logoUrl ?? null } : null;
    }

    const payload = {
      modoGlobal: novoModoGlobal,
      empresaAtual: empresa,
    };

    window.localStorage.setItem(storageKey, JSON.stringify(payload));
    setModoGlobal(novoModoGlobal);
    setEmpresaAtual(empresa);
  };

  const definirGlobal = () => {
    salvarPreferencia(true, null);
  };

  const definirEmpresa = (empresa) => {
    // aceita {id, nome} ou string (nome)
    salvarPreferencia(false, empresa);
  };

  const value = {
    modoGlobal,
    empresaAtual,
    definirGlobal,
    definirEmpresa,
  };

  return (
    <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
  );
}

export function useEmpresa() {
  return useContext(EmpresaContext);
}
